/* kic-toasts.js - Wave B comp 26-27 theme-port.
   Public API: window.KIC.toast.show({ variant, thumb, eyebrow, title, meta,
   oneOfOne, action, ttl }). Variants: cart | sold | error.
   - cart: role=status + aria-live=polite, ttl 5000
   - sold: role=status + aria-live=polite, ttl 5000, frequency-capped
   - error: role=alert + aria-live=assertive, ttl 8000
   Stack max 3 FIFO. Pause on hover/focus-within. Suppression on PDP/cart/checkout
   for sold variant. Hooks Shopify cart/add fetch + theme cart events.
   Uses textContent / createElement throughout - no innerHTML, no XSS surface. */
(function () {
  'use strict';

  var ROOT_ID = 'kic-toast-root';
  var ANNOUNCER_ID = 'kic-toast-announcer';
  var MAX_VISIBLE = 3;
  var DEFAULT_TTL = 5000;
  var ERROR_TTL = 8000;
  var MAX_SOLD_PER_60S = 1;
  var MAX_SOLD_PER_SESSION = 3;
  var SOLD_KEY_RECENT = 'kic_sold_recent_at';
  var SOLD_KEY_COUNT = 'kic_sold_session_count';
  var SOLD_KEY_DISMISS = 'kic_sold_dismiss_count';

  function el(tag, attrs, text) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (Object.prototype.hasOwnProperty.call(attrs, k) && attrs[k] != null) {
          if (k === 'class') node.className = attrs[k];
          else node.setAttribute(k, attrs[k]);
        }
      }
    }
    if (text != null) node.textContent = String(text);
    return node;
  }

  function mountRoot() {
    var root = document.getElementById(ROOT_ID);
    if (!root) {
      root = el('div', { id: ROOT_ID, 'class': 'kic-toast-root kic-mi-root' });
      document.body.appendChild(root);
    }
    var announcer = document.getElementById(ANNOUNCER_ID);
    if (!announcer) {
      announcer = el('span', {
        id: ANNOUNCER_ID,
        'class': 'kic-toast__announcer',
        'aria-live': 'polite',
        'aria-atomic': 'true'
      });
      document.body.appendChild(announcer);
    }
    return { root: root, announcer: announcer };
  }

  function isSuppressedRoute() {
    var p = window.location.pathname || '';
    return /^\/products\//.test(p) || /^\/cart/.test(p) || /^\/checkout/.test(p);
  }

  function readSession(key, fallback) {
    try {
      var v = window.sessionStorage.getItem(key);
      return v == null ? fallback : v;
    } catch (e) { return fallback; }
  }

  function writeSession(key, value) {
    try { window.sessionStorage.setItem(key, String(value)); } catch (e) {}
  }

  function shouldSuppressSold() {
    if (isSuppressedRoute()) return true;
    var now = Date.now();
    var recent = parseInt(readSession(SOLD_KEY_RECENT, '0'), 10) || 0;
    if (recent !== 0 && now - recent < 60000 * MAX_SOLD_PER_60S) return true;
    var count = parseInt(readSession(SOLD_KEY_COUNT, '0'), 10) || 0;
    if (count >= MAX_SOLD_PER_SESSION) return true;
    var prefersReduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var dismissed = parseInt(readSession(SOLD_KEY_DISMISS, '0'), 10) || 0;
    if (prefersReduced && dismissed >= 2) return true;
    return false;
  }

  function recordSoldShown() {
    writeSession(SOLD_KEY_RECENT, Date.now());
    var count = parseInt(readSession(SOLD_KEY_COUNT, '0'), 10) || 0;
    writeSession(SOLD_KEY_COUNT, count + 1);
  }

  function recordSoldDismissed() {
    var dismissed = parseInt(readSession(SOLD_KEY_DISMISS, '0'), 10) || 0;
    writeSession(SOLD_KEY_DISMISS, dismissed + 1);
  }

  function buildToastEl(opts) {
    var variant = opts.variant || 'cart';
    var classes = ['kic-toast', 'kic-toast--enter'];
    if (variant === 'sold') classes.push('kic-toast--sold');
    if (variant === 'error') classes.push('kic-toast--error');

    var attrs = { 'class': classes.join(' '), 'aria-hidden': 'true', 'aria-atomic': 'true' };
    if (variant === 'error') {
      attrs.role = 'alert';
      attrs['aria-live'] = 'assertive';
    } else {
      attrs.role = 'status';
      attrs['aria-live'] = 'polite';
    }

    var node = el('div', attrs);

    // thumb
    if (opts.thumb && opts.thumb.src) {
      node.appendChild(el('img', {
        'class': 'kic-toast__thumb',
        src: opts.thumb.src,
        alt: opts.thumb.alt || '',
        width: opts.thumb.width || 60,
        height: opts.thumb.height || 75,
        loading: 'lazy',
        decoding: 'async'
      }));
    } else {
      node.appendChild(el('div', { 'class': 'kic-toast__thumb', 'aria-hidden': 'true' }));
    }

    var body = el('div', { 'class': 'kic-toast__body' });

    if (opts.oneOfOne) {
      var ooRow = el('div', { 'class': 'kic-toast__eyebrow-row' });
      ooRow.appendChild(el('span', { 'class': 'kic-toast__one-of-one' }, 'One of one'));
      body.appendChild(ooRow);
    }

    body.appendChild(el('p', { 'class': 'kic-toast__micro' }, opts.eyebrow || ''));
    body.appendChild(el('p', { 'class': 'kic-toast__title' }, opts.title || ''));

    if (opts.meta) {
      body.appendChild(el('p', { 'class': 'kic-toast__meta' }, opts.meta));
    }
    if (opts.action && opts.action.label && opts.action.href) {
      var a = el('a', {
        'class': 'kic-toast__action',
        href: opts.action.href,
        'aria-label': opts.action.label
      }, opts.action.label + ' →');
      body.appendChild(a);
    }
    node.appendChild(body);

    var close = el('button', {
      type: 'button',
      'class': 'kic-toast__close',
      'aria-label': 'Dismiss notification'
    }, '×');
    node.appendChild(close);

    node.appendChild(el('div', { 'class': 'kic-toast__progress', 'aria-hidden': 'true' }));

    return node;
  }

  function show(opts) {
    if (!opts || typeof opts !== 'object') return null;
    if (opts.variant === 'sold' && shouldSuppressSold()) return null;

    var mounted = mountRoot();
    var root = mounted.root;
    var announcer = mounted.announcer;

    var existing = root.querySelectorAll('.kic-toast');
    while (existing.length >= MAX_VISIBLE) {
      var oldest = existing[0];
      oldest.classList.add('kic-toast--exit');
      (function (n) {
        setTimeout(function () { if (n && n.parentNode) n.parentNode.removeChild(n); }, 140);
      })(oldest);
      existing = root.querySelectorAll('.kic-toast');
      if (existing.length <= 0) break;
    }

    var ttl = typeof opts.ttl === 'number'
      ? opts.ttl
      : (opts.variant === 'error' ? ERROR_TTL : DEFAULT_TTL);

    var node = buildToastEl(opts);

    if (ttl !== 5000) {
      var styleId = 'kic-toast-style-' + Math.random().toString(36).slice(2, 9);
      node.dataset.kicToastStyle = styleId;
      var styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent =
        '[data-kic-toast-style="' + styleId + '"] .kic-toast__progress::after { animation-duration: ' + ttl + 'ms; }';
      document.head.appendChild(styleEl);
    }

    root.appendChild(node);

    if (announcer) {
      announcer.setAttribute('aria-live', opts.variant === 'error' ? 'assertive' : 'polite');
      announcer.textContent = (opts.eyebrow ? opts.eyebrow + '. ' : '') + (opts.title || '');
    }

    if (opts.variant === 'sold') recordSoldShown();

    var dismissTimer = null;
    var elapsed = 0;
    var startedAt = Date.now();
    var paused = false;

    function dismiss() {
      if (!node || !node.parentNode) return;
      node.classList.add('kic-toast--exit');
      setTimeout(function () {
        if (node && node.parentNode) node.parentNode.removeChild(node);
        var sid = node && node.dataset && node.dataset.kicToastStyle;
        if (sid) {
          var sn = document.getElementById(sid);
          if (sn && sn.parentNode) sn.parentNode.removeChild(sn);
        }
      }, 140);
    }

    function startTimer(remaining) {
      if (dismissTimer) clearTimeout(dismissTimer);
      startedAt = Date.now();
      dismissTimer = setTimeout(dismiss, remaining);
    }

    function pause() {
      if (paused) return;
      paused = true;
      elapsed += (Date.now() - startedAt);
      if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = null; }
    }

    function resume() {
      if (!paused) return;
      paused = false;
      var remaining = Math.max(400, ttl - elapsed);
      startTimer(remaining);
    }

    node.addEventListener('mouseenter', pause);
    node.addEventListener('mouseleave', resume);
    node.addEventListener('focusin', pause);
    node.addEventListener('focusout', function (ev) {
      if (!node.contains(ev.relatedTarget)) resume();
    });

    var closeBtn = node.querySelector('.kic-toast__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        if (opts.variant === 'sold') recordSoldDismissed();
        dismiss();
      });
    }

    node.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') {
        if (opts.variant === 'sold') recordSoldDismissed();
        dismiss();
      }
    });

    startTimer(ttl);
    return node;
  }

  // -----------------------------------------------------------------------
  // Cart-add interception
  // -----------------------------------------------------------------------

  function pickProductImageFromForm(form) {
    if (!form || !form.closest) return null;
    var card = form.closest('[data-product-card], .product, .card-wrapper');
    var img = card ? card.querySelector('img') : null;
    if (!img) img = document.querySelector('.product__media img, [data-product-media] img');
    if (!img) return null;
    return { src: img.currentSrc || img.src, alt: '', width: 60, height: 75 };
  }

  function pickProductTitleFromForm(form) {
    if (!form || !form.closest) return '';
    var card = form.closest('[data-product-card], .product, .card-wrapper');
    var heading = card ? card.querySelector('h2, h3, .card__heading, [data-product-title]') : null;
    if (heading) return (heading.textContent || '').trim();
    var pdpTitle = document.querySelector('.product__title, h1.product-title, [data-product-title]');
    if (pdpTitle) return (pdpTitle.textContent || '').trim();
    return '';
  }

  function isOneOfOne(form) {
    if (!form || !form.closest) {
      return !!document.querySelector('[data-one-of-one], .kic-one-of-one');
    }
    var card = form.closest('[data-product-card], .product, .card-wrapper');
    var hasBadge = (card && card.querySelector('[data-one-of-one], .kic-one-of-one')) ||
                   document.querySelector('[data-one-of-one], .kic-one-of-one');
    return !!hasBadge;
  }

  function showCartError(message) {
    show({
      variant: 'error',
      eyebrow: 'Could not add to cart',
      title: message || 'That item is unavailable.',
      ttl: ERROR_TTL
    });
  }

  // R4.5: window.fetch monkey-patch removed (was double-firing toasts with the
  // cart:added DOM listener). Single ingestion path: PUB_SUB_EVENTS.cartUpdate
  // (Dawn pubsub, mirrors kic-bottom-nav.js). The cart:added DOM listener is
  // also retired here; pubsub is the canonical add-to-cart signal Shopify Dawn
  // publishes after every successful /cart/add.js call. Polling-binding pattern
  // matches kic-bottom-nav.js — defer load order may not have pubsub.js ready
  // at IIFE-eval time.

  // R10.5 (2026-05-01) — idempotent bind flag. The polling loop below
  // (line ~354) keeps calling bindCartUpdate() on a setInterval until it
  // returns true. If pubsub.js initialises mid-poll and a synchronous
  // /cart/add.js fires before clearInterval lands, two subscribers can be
  // registered against the same cartUpdate channel. Result: toast double.
  // The flag below short-circuits the second + subsequent successful binds.
  var _kicToastCartBound = false;
  function bindCartUpdate() {
    if (_kicToastCartBound) return true;
    // pubsub.js declares `function subscribe`/`function publish` (these attach
    // to window in classic-script load), but constants.js declares
    // `const PUB_SUB_EVENTS` which does NOT attach to window. Reference the
    // bare global instead. Same fix should land on kic-bottom-nav.js.
    var hasSub = typeof window.subscribe === 'function';
    var hasEvents = typeof PUB_SUB_EVENTS !== 'undefined';
    if (!hasSub || !hasEvents) return false;
    if (!PUB_SUB_EVENTS.cartUpdate) return false;
    _kicToastCartBound = true;
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var src = event && event.source;
      // Only fire on explicit add-to-cart (Dawn 'product-form' source).
      // 'cart-items' = quantity edits / removals — no toast.
      if (src !== 'product-form') return;
      var data = (event && event.cartData) || {};
      var title = '';
      if (data.items && data.items.length) {
        title = data.items[data.items.length - 1].product_title || '';
      }
      show({
        variant: 'cart',
        eyebrow: 'Added to cart',
        title: title,
        action: { label: 'View cart', href: '/cart' },
        ttl: 5000
      });
    });
    return true;
  }

  if (!bindCartUpdate()) {
    var cuAttempts = 0;
    var cuIv = setInterval(function () {
      cuAttempts += 1;
      if (bindCartUpdate() || cuAttempts > 20) clearInterval(cuIv);
    }, 150);
  }

  document.addEventListener('cart:error', function (ev) {
    var detail = (ev && ev.detail) || {};
    showCartError(detail.message);
  });

  window.KIC = window.KIC || {};
  window.KIC.toast = window.KIC.toast || {};
  window.KIC.toast.show = show;
  window.KIC.toast.cart = function (opts) {
    return show(Object.assign({
      variant: 'cart', eyebrow: 'Added to cart',
      action: { label: 'View cart', href: '/cart' }, ttl: 5000
    }, opts || {}));
  };
  window.KIC.toast.sold = function (opts) {
    return show(Object.assign({ variant: 'sold', eyebrow: 'Sold', ttl: 5000 }, opts || {}));
  };
  window.KIC.toast.error = function (opts) {
    return show(Object.assign({
      variant: 'error', eyebrow: 'Could not add to cart', ttl: ERROR_TTL
    }, opts || {}));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountRoot);
  } else {
    mountRoot();
  }
})();
